import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useTrackOticAuth } from '@/context/TrackOticAuthContext';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Shield, Building2, MapPin, Users, Key, Wifi, Bell, 
  FileText, CreditCard, LogOut, ChevronLeft, ChevronRight,
  LayoutDashboard, Settings
} from 'lucide-react';

const menuItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/control/dashboard' },
  { id: 'tenants', label: 'Customers', icon: Building2, path: '/control/tenants' },
  { id: 'devices', label: 'Device Control', icon: Wifi, path: '/control/devices' },
  { id: 'alerts', label: 'Global Alerts', icon: Bell, path: '/control/alerts' },
  { id: 'audit', label: 'Audit Logs', icon: FileText, path: '/control/audit' },
  { id: 'billing', label: 'Billing', icon: CreditCard, path: '/control/billing' },
  { id: 'settings', label: 'Settings', icon: Settings, path: '/control/settings' },
];

export default function ControlLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const { user, tenant, logout, loading } = useTrackOticAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && (!user || tenant?.code !== 'SENTINELX')) {
      navigate('/control/login');
    }
  }, [user, tenant, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate('/control/login');
  };

  return (
    <div className="min-h-screen bg-slate-950 flex">
      {/* Sidebar */}
      <aside className={cn(
        "bg-slate-900 border-r border-slate-800 flex flex-col transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {!collapsed && (
            <div className="flex items-center gap-2">
              <Shield className="w-8 h-8 text-blue-500" />
              <span className="font-bold text-white">Track<span className="text-red-500">0</span>tic Control</span>
            </div>
          )}
          {collapsed && <Shield className="w-8 h-8 text-blue-500 mx-auto" />}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setCollapsed(!collapsed)}
            className="text-slate-400 hover:text-white"
            data-testid="sidebar-toggle"
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);
            return (
              <Link
                key={item.id}
                to={item.path}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                  isActive 
                    ? "bg-blue-600 text-white" 
                    : "text-slate-400 hover:text-white hover:bg-slate-800"
                )}
                data-testid={`nav-${item.id}`}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            );
          })}
        </nav>

        {/* User section */}
        <div className="p-4 border-t border-slate-800">
          {!collapsed && (
            <div className="mb-3">
              <p className="text-white font-medium truncate">{user?.full_name}</p>
              <p className="text-slate-500 text-sm truncate">{user?.email}</p>
            </div>
          )}
          <Button
            variant="ghost"
            className={cn(
              "text-slate-400 hover:text-white hover:bg-slate-800",
              collapsed ? "w-full justify-center" : "w-full justify-start"
            )}
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            {!collapsed && <span className="ml-2">Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <Outlet />
      </main>
    </div>
  );
}
